<template>
  <PageLayout title="QLC Macros">
    <template #actions>
      <button class="btn btn-primary btn-sm" @click="openCreate">
        <i class="fas fa-plus mr-1"></i> New Sequence
      </button>
    </template>

    <ContentCard>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <p class="text-muted mb-0">
          Urutan tombol QLC+ — klik A → B → C, simpan, lalu jalankan sekali.
        </p>
      </div>

      <div v-if="!qlcMacros.length" class="text-center text-muted py-5">
        <i class="fas fa-lightbulb fa-2x mb-3 d-block"></i>
        <p>Belum ada QLC sequence.</p>
        <button class="btn btn-outline-primary btn-sm" @click="openCreate">Buat sequence pertama</button>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Sequence</th>
              <th>Steps</th>
              <th class="text-right" style="width:200px">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in qlcMacros" :key="m.id">
              <td>
                <span
                  class="qlc-swatch mr-2"
                  :style="{ background: m.color, color: previewText(m.color) }"
                >
                  <i class="fas fa-lightbulb"></i>
                </span>
                <strong>{{ m.name }}</strong>
              </td>
              <td>
                <span class="qlc-seq-preview text-muted small">{{ sequencePreview(m) }}</span>
              </td>
              <td>
                <span class="badge badge-light border">{{ buttonStepCount(m) }} btn</span>
              </td>
              <td class="text-right text-nowrap">
                <button
                  class="btn btn-sm btn-outline-primary mr-1"
                  :disabled="runningId === m.id"
                  @click="run(m)"
                >
                  <i class="fas" :class="runningId === m.id ? 'fa-spinner fa-spin' : 'fa-play'"></i>
                </button>
                <button class="btn btn-sm btn-outline-secondary mr-1" @click="openEdit(m)">
                  <i class="fas fa-pen"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(m)">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>

    <modal name="edit-qlc-macro" height="auto" :width="720" :adaptive="true" :scrollable="true">
      <div class="p-4 qlc-modal">
        <h5 class="mb-3">{{ form.id ? 'Edit Sequence' : 'New Sequence' }}</h5>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label class="small text-muted">Name</label>
            <input v-model="form.name" class="form-control" maxlength="100" placeholder="e.g. Opening lights">
          </div>
          <div class="form-group col-md-6">
            <label class="small text-muted">QLC device</label>
            <select v-model="form.nodeId" class="form-control" @change="onNodeChange">
              <option value="">-- Select node --</option>
              <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="small text-muted">Delay antar tombol (ms)</label>
            <input v-model.number="form.defaultDelay" type="number" min="0" max="60000" step="50" class="form-control">
          </div>
          <div class="form-group col-md-4 d-flex align-items-end">
            <div class="custom-control custom-switch mb-2">
              <input id="qlc-stop" type="checkbox" class="custom-control-input" v-model="form.stopOnError">
              <label class="custom-control-label" for="qlc-stop">Stop on error</label>
            </div>
          </div>
        </div>

        <div class="qlc-builder">
          <div class="qlc-builder__palette">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong class="small">Klik tombol untuk menambah</strong>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="!form.nodeId || buttonsLoading"
                @click="loadButtons(true)"
              >
                <i class="fas" :class="buttonsLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
              </button>
            </div>
            <div v-if="!form.nodeId" class="text-muted small">Pilih device dulu.</div>
            <div v-else-if="buttonsLoading" class="text-muted small">Loading…</div>
            <div v-else-if="buttonsError" class="text-danger small">{{ buttonsError }}</div>
            <div v-else-if="!buttons.length" class="text-muted small">Tidak ada tombol.</div>
            <div v-else class="qlc-palette-grid">
              <button
                v-for="b in buttons"
                :key="b.id"
                type="button"
                class="qlc-palette-btn"
                @click="addButton(b)"
              >
                {{ b.name || `Button ${b.id}` }}
              </button>
            </div>
          </div>

          <div class="qlc-builder__seq">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong class="small">Sequence</strong>
              <button
                v-if="form.steps.length"
                type="button"
                class="btn btn-sm btn-link text-danger p-0"
                @click="form.steps = []"
              >
                Clear
              </button>
            </div>
            <div v-if="!form.steps.length" class="text-muted small py-3 text-center">
              Belum ada step — klik tombol di kiri.
            </div>
            <ol v-else class="qlc-step-list">
              <li v-for="(step, idx) in form.steps" :key="idx" class="qlc-step">
                <span class="qlc-step__num">{{ idx + 1 }}</span>
                <template v-if="step.kind === 'delay'">
                  <span class="qlc-step__label">Delay</span>
                  <input
                    v-model.number="step.delayMs"
                    type="number"
                    min="0"
                    max="60000"
                    step="50"
                    class="form-control form-control-sm qlc-step__delay"
                  >
                  <span class="small text-muted">ms</span>
                </template>
                <template v-else>
                  <span class="qlc-step__label">{{ stepLabel(step) }}</span>
                  <select v-model.number="step.payload.value" class="form-control form-control-sm qlc-step__val">
                    <option :value="255">ON</option>
                    <option :value="0">OFF</option>
                  </select>
                </template>
                <div class="qlc-step__actions">
                  <button type="button" class="btn btn-sm btn-link p-0" :disabled="idx === 0" @click="moveStep(idx, -1)">
                    <i class="fas fa-arrow-up"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-link p-0"
                    :disabled="idx === form.steps.length - 1"
                    @click="moveStep(idx, 1)"
                  >
                    <i class="fas fa-arrow-down"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="removeStep(idx)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </li>
            </ol>
            <button
              v-if="form.steps.length"
              type="button"
              class="btn btn-sm btn-outline-secondary mt-2"
              @click="addDelay"
            >
              <i class="fas fa-clock mr-1"></i> Add delay
            </button>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-light mr-2" @click="$modal.hide('edit-qlc-macro')">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="saving || !canSave"
            @click="save"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </modal>

    <modal name="delete-qlc-macro" height="auto" :width="400" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-2">Delete Sequence</h5>
        <p class="mb-3">Hapus <strong>{{ deleteTarget && deleteTarget.name }}</strong>?</p>
        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('delete-qlc-macro')">Cancel</button>
          <button class="btn btn-danger" :disabled="deleting" @click="doDelete">
            {{ deleting ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  contrastText,
  qlcButtonStep,
  qlcDelayStep,
  QLC_MACRO_ICON,
  QLC_MACRO_COLOR,
} from '@/widgets/macros'
import { getQlcButtons, peekQlcButtons } from '@/widgets/qlcCache'

function blankForm() {
  return {
    id: null,
    name: '',
    nodeId: '',
    defaultDelay: 300,
    stopOnError: true,
    steps: [],
  }
}

export default {
  data() {
    return {
      form: blankForm(),
      buttons: [],
      buttonsLoading: false,
      buttonsError: '',
      saving: false,
      runningId: null,
      deleteTarget: null,
      deleting: false,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    ...mapGetters('macro', ['qlcMacros']),
    canSave() {
      return !!this.form.name.trim()
        && !!this.form.nodeId
        && this.form.steps.some((s) => s.kind === 'command')
    },
  },
  async mounted() {
    await Promise.all([this.fetchMacros(), this.fetchNodes()])
  },
  methods: {
    ...mapActions('macro', ['fetchMacros', 'createMacro', 'updateMacro', 'deleteMacro', 'runMacro']),
    ...mapActions('mission', ['fetchNodes', 'sendCommand']),
    previewText: contrastText,
    buttonStepCount(m) {
      return (m.steps || []).filter((s) => s.kind === 'command').length
    },
    sequencePreview(m) {
      const parts = []
      ;(m.steps || []).forEach((s) => {
        if (s.kind === 'delay') parts.push(`${s.delayMs || 0}ms`)
        else if (s.kind === 'command') {
          const label = s.payload?._label || `ID ${s.payload?.widgetId}`
          const off = Number(s.payload?.value) === 0 ? '↓' : ''
          parts.push(`${label}${off}`)
        }
      })
      return parts.join(' → ') || '—'
    },
    stepLabel(step) {
      return step.payload?._label || `Button ${step.payload?.widgetId}`
    },
    openCreate() {
      this.form = blankForm()
      if (this.nodes.length === 1) {
        this.form.nodeId = this.nodes[0].id
        this.loadButtons()
      }
      this.buttons = []
      this.buttonsError = ''
      this.$modal.show('edit-qlc-macro')
    },
    openEdit(m) {
      const cmds = (m.steps || []).filter((s) => s.kind === 'command')
      const nodeId = cmds[0]?.nodeId || ''
      this.form = {
        id: m.id,
        name: m.name,
        nodeId,
        defaultDelay: 300,
        stopOnError: m.stopOnError !== false,
        steps: (m.steps || []).map((s) => {
          if (s.kind === 'delay') return { ...s }
          return {
            kind: 'command',
            nodeId: s.nodeId,
            adapter: 'qlc',
            action: 'setWidgetState',
            payload: {
              widgetId: Number(s.payload?.widgetId),
              value: s.payload?.value != null ? Number(s.payload.value) : 255,
              on: s.payload?.on != null
                ? !!s.payload.on
                : (s.payload?.value != null ? Number(s.payload.value) >= 200 : true),
              _label: s.payload?._label || `Button ${s.payload?.widgetId}`,
            },
          }
        }),
      }
      this.$modal.show('edit-qlc-macro')
      if (nodeId) this.loadButtons()
    },
    onNodeChange() {
      this.form.steps = this.form.steps.map((s) => {
        if (s.kind !== 'command') return s
        return { ...s, nodeId: this.form.nodeId }
      })
      this.loadButtons()
    },
    async loadButtons(force = false) {
      if (!this.form.nodeId) {
        this.buttons = []
        return
      }
      const cached = !force ? peekQlcButtons(this.form.nodeId) : null
      if (cached) {
        this.buttons = cached
        this.buttonsError = ''
        return
      }
      this.buttonsLoading = true
      this.buttonsError = ''
      try {
        this.buttons = await getQlcButtons(
          (payload) => this.sendCommand(payload),
          this.form.nodeId,
          { force: !!force },
        )
      } catch (e) {
        this.buttons = []
        this.buttonsError = e.message || 'Failed to load buttons'
      } finally {
        this.buttonsLoading = false
      }
    },
    addButton(b) {
      if (!this.form.nodeId || !b) return
      const steps = [...this.form.steps]
      if (steps.length && steps[steps.length - 1].kind === 'command' && this.form.defaultDelay > 0) {
        steps.push(qlcDelayStep(this.form.defaultDelay))
      }
      steps.push(qlcButtonStep(this.form.nodeId, b.id, b.name || `Button ${b.id}`, 255))
      this.form.steps = steps
    },
    addDelay() {
      this.form.steps.push(qlcDelayStep(this.form.defaultDelay || 300))
    },
    removeStep(idx) {
      this.form.steps.splice(idx, 1)
    },
    moveStep(idx, dir) {
      const next = idx + dir
      if (next < 0 || next >= this.form.steps.length) return
      const list = [...this.form.steps]
      const tmp = list[idx]
      list[idx] = list[next]
      list[next] = tmp
      this.form.steps = list
    },
    async save() {
      if (!this.canSave || this.saving) return
      this.saving = true
      try {
        const steps = this.form.steps.map((s) => {
          if (s.kind === 'delay') return qlcDelayStep(s.delayMs)
          return qlcButtonStep(
            this.form.nodeId,
            s.payload.widgetId,
            s.payload._label,
            s.payload.value,
          )
        })
        const payload = {
          name: this.form.name.trim(),
          color: QLC_MACRO_COLOR,
          icon: QLC_MACRO_ICON,
          stopOnError: this.form.stopOnError,
          steps,
        }
        if (this.form.id) {
          await this.updateMacro({ id: this.form.id, ...payload })
          this.$toast.success('Sequence updated')
        } else {
          await this.createMacro(payload)
          this.$toast.success('Sequence created')
        }
        this.$modal.hide('edit-qlc-macro')
      } catch (e) {
        this.$toast.error(e.response?.data?.message || e.message || 'Save failed')
      } finally {
        this.saving = false
      }
    },
    confirmDelete(m) {
      this.deleteTarget = m
      this.$modal.show('delete-qlc-macro')
    },
    async doDelete() {
      if (!this.deleteTarget || this.deleting) return
      this.deleting = true
      try {
        await this.deleteMacro(this.deleteTarget.id)
        this.$toast.success('Deleted')
        this.$modal.hide('delete-qlc-macro')
        this.deleteTarget = null
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Delete failed')
      } finally {
        this.deleting = false
      }
    },
    async run(m) {
      if (this.runningId) return
      this.runningId = m.id
      try {
        const res = await this.runMacro(m)
        if (res.failed) {
          this.$toast.warning(`Done ${res.done}/${res.total}, ${res.failed} failed`)
        } else {
          this.$toast.success(`Ran ${res.done} steps`)
        }
      } catch (e) {
        this.$toast.error(e.message || 'Run failed')
      } finally {
        this.runningId = null
      }
    },
  },
}
</script>

<style scoped>
.qlc-swatch {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  vertical-align: middle;
}
.qlc-seq-preview {
  display: inline-block;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qlc-builder {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  min-height: 280px;
}
.qlc-builder__palette,
.qlc-builder__seq {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  background: #fafbfc;
  min-height: 0;
  overflow: auto;
  max-height: 360px;
}
.qlc-palette-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.35rem;
}
.qlc-palette-btn {
  border: 1px solid #ced4da;
  background: #fff;
  border-radius: 6px;
  padding: 0.45rem 0.4rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qlc-palette-btn:hover {
  border-color: #128c7e;
  background: rgba(18, 140, 126, 0.08);
}
.qlc-step-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.qlc-step {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #eee;
}
.qlc-step__num {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background: #128c7e;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.qlc-step__label {
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qlc-step__delay {
  width: 90px;
}
.qlc-step__val {
  width: 72px;
}
.qlc-step__actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}
@media (max-width: 767.98px) {
  .qlc-builder {
    grid-template-columns: 1fr;
  }
}
</style>
